import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { useSidebar } from '../../context/SidebarContext';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { companionApi } from '../../api/facades';
import { useCompanionConsent } from '../../hooks/useCompanionConsent';
import CompanionConsentModal from '../Companion/CompanionConsentModal';
import {
  Home,
  Folder,
  Users,
  FileText,
  CheckCircle,
  Calendar,
  CreditCard,
  BarChart,
  Settings,
  Layout,
  Scale,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  X
} from '../../utils/icons';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string; size?: number }> | any;
  path: string;
  children?: NavigationItem[];
}

const navigationItems: NavigationItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' },
  { id: 'court-cases', label: 'Court Cases', icon: Folder, path: '/dashboard/court-cases' },
  { id: 'clients', label: 'Clients', icon: Users, path: '/dashboard/clients' },
  { id: 'documents', label: 'Documents', icon: FileText, path: '/dashboard/documents' },
  { id: 'workflows', label: 'Workflows', icon: Layout, path: '/dashboard/workflows' },
  { id: 'tasks', label: 'Tasks', icon: CheckCircle, path: '/dashboard/tasks' },
  { id: 'calendar', label: 'Calendar', icon: Calendar, path: '/dashboard/calendar' },
  { id: 'appointments', label: 'Appointments', icon: Layout, path: '/dashboard/appointments' },
  { id: 'legal-forms', label: 'Forms & Precedents', icon: FileText, path: '/dashboard/legal-forms' },
  { id: 'budget', label: 'Budget', icon: Scale, path: '/dashboard/budget' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/dashboard/billing' },
  { id: 'invoices', label: 'Invoices', icon: CreditCard, path: '/dashboard/invoices' },
  { id: 'expenses', label: 'Expenses', icon: CreditCard, path: '/dashboard/expenses' },
  { id: 'reports', label: 'Reports', icon: BarChart, path: '/dashboard/reports' },
  { id: 'companion', label: 'Companion', icon: MessageSquare, path: '/dashboard' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/dashboard/settings' },
];

const LeftSidebar: React.FC = () => {
  const { isOpen, closeSidebar, isCollapsed, toggleCollapsed, isHoverExpanded, setHoverExpanded } = useSidebar();
  const { theme } = useTheme();
  const { user } = useAuth();
  const roles = (user as any)?.roles || [];
  const permissions = (user as any)?.permissions || [];
  const hasManageSettings =
    Array.isArray(permissions) && permissions.some((p: any) => p?.slug === 'manage_settings');
  const isAdminRole =
    Array.isArray(roles) && roles.some((r: any) => String(r?.slug).toLowerCase() === 'admin');
  const canAccessSettings = hasManageSettings || isAdminRole;
  const XIcon = X as React.ComponentType<{ className?: string; size?: number; onClick?: () => void }>;
  const ChevronLeftIcon = ChevronLeft as React.ComponentType<{ size?: number; className?: string }>;
  const ChevronRightIcon = ChevronRight as React.ComponentType<{ size?: number; className?: string }>;

  const canHover = useMemo(() => {
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch {
      return false;
    }
  }, []);

  const effectiveCollapsed = isCollapsed && !isHoverExpanded;
  const logoSrc = theme === 'dark' ? '/light%20green.png' : '/green.png';

  const companionLinked = Number((user as any)?.companion_id ?? 0) > 0;
  const { showModal, requestConsent, onAccept, onDecline } = useCompanionConsent();

  const doOpenCompanion = async () => {
    if (!companionLinked) {
      toast.error('Link Click Companion from the dashboard first.');
      return;
    }
    try {
      const res = await companionApi.ssoUrl();
      const url = res?.data?.data?.url;
      if (!url) throw new Error(res?.data?.message || 'Could not open Companion.');
      window.open(url, '_blank', 'noopener,noreferrer');
      closeSidebar();
    } catch (e: any) {
      toast.error(e?.message || 'Could not open Companion.');
    }
  };

  const openCompanion = () => requestConsent(doOpenCompanion);

  return (
    <>
      <aside 
        onMouseEnter={() => {
          if (!canHover) return;
          if (!isCollapsed) return;
          setHoverExpanded(true);
        }}
        onMouseLeave={() => {
          if (!canHover) return;
          setHoverExpanded(false);
        }}
        className={`ease-nav-brand z-990 fixed inset-y-0 my-4 ml-4 flex h-[calc(100%-2rem)] w-full flex-col rounded-2xl border-0 p-0 antialiased transition-all duration-200 xl:left-0 xl:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full xl:translate-x-0'
        } ${effectiveCollapsed ? 'xl:w-24 xl:max-w-24' : 'xl:w-62.5 xl:max-w-62.5'} ${
          isOpen ? 'bg-white/70 dark:bg-slate-800/70 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-800/60 xl:bg-transparent dark:xl:bg-transparent' : 'bg-transparent'
        }`}
      >
        <div className="flex-none">
          <XIcon 
            className="absolute top-0 right-0 p-4 opacity-50 cursor-pointer text-slate-400 dark:text-slate-500 xl:hidden" 
            onClick={closeSidebar}
            size={20}
          />
          <div
            className={`z-20 flex items-center justify-between py-6 ${
              effectiveCollapsed ? 'px-3' : 'px-8'
            }`}
          >
            <NavLink
              className="flex min-w-0 items-center m-0 text-sm whitespace-nowrap text-slate-700 dark:text-slate-200"
              to="/dashboard"
              onClick={closeSidebar}
            >
              <img
                src={logoSrc}
                alt="ClickLaw"
                className="h-12 w-12 rounded-lg bg-transparent object-contain"
              />
              {/* <span className={`ml-2 font-semibold transition-all duration-200 ease-nav-brand ${effectiveCollapsed ? 'xl:hidden' : ''}`}>
                ClickLaw
              </span> */}
            </NavLink>

            <button
              type="button"
              onClick={toggleCollapsed}
              className="relative z-30 hidden xl:inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 hover:bg-gray-100/50 dark:hover:bg-slate-700/50 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <span className="inline-flex items-center justify-center">
                {isCollapsed ? (
                  <ChevronRightIcon className="transition-transform duration-200" size={20} />
                ) : (
                  <ChevronLeftIcon className="transition-transform duration-200" size={20} />
                )}
              </span>
            </button>
          </div>
        </div>

        <hr className="h-px mt-0 bg-transparent bg-gradient-to-r from-transparent via-black/40 dark:via-white/20 to-transparent" />

        <div className="clicklaw-scrollbar min-h-0 flex-1 overflow-y-auto pb-4">
          <ul className="flex flex-col pl-0 mb-0">
            {navigationItems
              .filter((item) => (item.id === 'settings' ? canAccessSettings : true))
              .map((item) => (
              <li key={item.id} className="mt-0.5 w-full">
                {item.id === 'companion' ? (
                  <button
                    type="button"
                    title={
                      companionLinked
                        ? item.label
                        : 'Link Click Companion from the dashboard first'
                    }
                    disabled={!companionLinked}
                    aria-disabled={!companionLinked}
                    onClick={openCompanion}
                    className={`py-2.7 text-sm ease-nav-brand my-0 mx-4 flex w-[calc(100%-2rem)] items-center whitespace-nowrap px-4 transition-colors xl:justify-start rounded-lg ${
                      companionLinked
                        ? 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-white/60 dark:hover:bg-slate-800/60'
                        : 'cursor-not-allowed text-slate-400 dark:text-slate-600 opacity-60'
                    } ${effectiveCollapsed ? 'xl:mx-2 xl:px-2 xl:justify-center' : ''}`}
                  >
                    <div
                      className={`mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5 bg-transparent ${
                        effectiveCollapsed ? 'xl:mr-0' : ''
                      }`}
                    >
                      <item.icon
                        className={
                          companionLinked
                            ? 'text-xs text-slate-600 dark:text-slate-400'
                            : 'text-xs text-slate-400 dark:text-slate-600'
                        }
                        size={16}
                      />
                    </div>
                    <span className={`ml-1 duration-300 opacity-100 pointer-events-none ease-soft ${effectiveCollapsed ? 'xl:hidden' : ''}`}>
                      {item.label}
                    </span>
                  </button>
                ) : (
                <NavLink
                  to={item.path}
                  end={item.id === 'dashboard'}
                  title={item.label}
                  className={({ isActive }) =>
                    `py-2.7 text-sm ease-nav-brand my-0 mx-4 flex items-center whitespace-nowrap px-4 transition-colors xl:justify-start ${
                      isActive ? 'font-semibold text-slate-700 dark:text-slate-100 rounded-lg' : 'text-slate-700 dark:text-slate-300'
                    } ${effectiveCollapsed ? 'xl:mx-2 xl:px-2 xl:justify-center' : ''}`
                  }
                  onClick={closeSidebar}
                >
                  {({ isActive }) => (
                    <>
                      <div
                        className={`mr-2 flex h-8 w-8 items-center justify-center rounded-lg bg-center stroke-0 text-center xl:p-2.5 ${
                          isActive ? 'bg-primary-700' : 'bg-transparent'
                        } ${effectiveCollapsed ? 'xl:mr-0' : ''}`}
                      >
                        <item.icon className={`text-xs ${isActive ? 'text-white' : 'text-slate-600 dark:text-slate-400'}`} size={16} />
                      </div>
                      <span className={`ml-1 duration-300 opacity-100 pointer-events-none ease-soft ${effectiveCollapsed ? 'xl:hidden' : ''}`}>
                        {item.label}
                      </span>
                    </>
                  )}
                </NavLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      </aside>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/60 z-980 xl:hidden"
          onClick={closeSidebar}
        ></div>
      )}

      <CompanionConsentModal open={showModal} onAccept={onAccept} onDecline={onDecline} />
    </>
  );
};

export default LeftSidebar;
